/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';

import { IoIosArrowBack, IoMdInformationCircle } from 'react-icons/io';
import { IoCopy } from 'react-icons/io5';
import { useHistory, useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { toast } from 'react-toastify';

import { useAppClipBoardManual } from '~/hooks/clipBoardManual';
import { useMuscleGroup } from '~/hooks/muscleGroup';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import { formatProfileCode } from '~/utils/formatProfileCode';

import {
  DivisionData,
  DivisionDataResults,
} from '../../Basic/TrainingProtocol/PrescriptionScreen/types';
import { IProgram } from '../../interface';
import { Card } from '../Card';
import Modal from './Modal';

import {
  ModalContainer,
  ContentContainer,
  RowContent,
  PageHeader,
  HeaderWrapper,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  Info,
  InfoContent,
  InfoButton,
  Container,
  AlertContainer,
  Section,
  PageHeaderContainer,
} from './styles';

type Result = {
  id: number;
  muscle_group: string;
  training_program_id: number;
};

type ProgramData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: Result[];
};

type ExEditorExercises = {
  name: string;
  id: number;
};

type ExEditor = {
  muscle_segment: string;
  exercicies: ExEditorExercises[];
};

type RouteProps = {
  userId: string;
  billingId: string;
  programId: string;
};

type Props = {
  lockScreen: (value: boolean) => void;
  refreshProgram: () => Promise<void>;
};

export default function TrainingDivisionScreen({
  lockScreen,
  refreshProgram,
}: Props) {
  const { userId: studentId, billingId, programId } = useParams<RouteProps>();
  const { push } = useHistory();
  const { studentData, updateStudent } = useStudent();
  const { updateGroup } = useMuscleGroup();
  const { setClipBoard } = useAppClipBoardManual();

  const [program, setProgram] = useState({} as IProgram);
  const [loading, setLoading] = useState(false);
  const [divisionId, setDivisionId] = useState<number>();
  const [trainingProgramId, setTrainingProgramId] = useState<number>();
  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false);
  const [divisions, setDivisions] = useState<DivisionDataResults[]>([]);
  const [sheetModelModal, setSheetModelModal] = useState(false);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>(
    [],
  );
  const [checkMuscleGroupOptions, setCheckMuscleGroupOptions] = useState<
    string[]
  >([]);
  const [defaultSelectedGroups, setDefaultSelectedGroups] = useState<string[]>(
    [],
  );

  const possibleToCopy = useMemo(
    () => Object.keys(program).length > 0 || null,
    [program],
  );

  const handleGoBack = useCallback(() => {
    push(`/professional/students/${studentId}`, {
      component: 'trainingPrograms',
    });
  }, [push, studentId]);

  useEffect(() => {
    updateStudent(studentId);
    defaultSelectedGroups.length ? lockScreen(false) : false;
  }, [updateStudent, studentId, defaultSelectedGroups, lockScreen]);

  // Carrega a lista de grupos muscular
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<ExEditor[]>(`ex-editors`);

      const mappedMuscleGroups = data.map((group) => group.muscle_segment);

      setCheckMuscleGroupOptions(mappedMuscleGroups);
    }

    fetchData();
  }, [program]);

  // Carrega a lista de grupos selecionados anteriormente
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<ProgramData>(
        `training-programs/${program.id}/divisions`,
      );

      const mappedGroups = data?.results.map((group) => group.muscle_group)[0];

      const groups = mappedGroups ? mappedGroups.split(`,`) : [];
      setDefaultSelectedGroups(groups);
      setSelectedMuscleGroups(groups);
      updateGroup(groups);

      const divId = data?.results.map((result) => result.id)[0];
      const trainingProgId = data?.results.map(
        (result) => result.training_program_id,
      )[0];

      divId && setDivisionId(divId);
      trainingProgId && setTrainingProgramId(trainingProgId);
      groups.length ? lockScreen(false) : false;

      setLoading(false);
    }

    fetchData();
  }, [program, lockScreen, updateGroup]);

  const handleGetDivisions = useCallback(async () => {
    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setDivisions(response.data.results);
    setProgram(response.data.header);

    if (response.data.results.length < 1) {
      setSheetModelModal(true);
    }
  }, [programId]);

  useEffect(() => {
    handleGetDivisions();
  }, [handleGetDivisions]);

  function StudentInfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal
        isOpen={showStudentInfoModal}
        setOpen={setShowStudentInfoModal}
        maxWidthStyled={600}
      >
        <ModalContainer>
          <h1>Informações do Treino</h1>

          <ContentContainer>
            <RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </RowContent>

            <RowContent>
              <strong>Programa: </strong>
              {programId}
            </RowContent>

            <RowContent>
              <strong>Nome do aluno: </strong>
              {studentData.name}
            </RowContent>

            <RowContent>
              <strong>Sexo: </strong>
              {genderVariations[studentData.student.genre]}
            </RowContent>

            <RowContent>
              <strong>Nível do aluno: </strong>
              {program?.datasheet?.student_level}
            </RowContent>

            <RowContent>
              <strong>Avaliação: </strong>
              {program?.datasheet?.classification}
            </RowContent>

            <RowContent>
              <strong>Objetivo: </strong>
              {program?.datasheet?.objective}
            </RowContent>

            <RowContent>
              <strong>Frequência: </strong>
              {program?.datasheet?.frequency}
            </RowContent>

            <RowContent>
              <strong>Tempo disponível: </strong>
              {program?.datasheet?.available_time}
            </RowContent>

            <RowContent>
              <strong>Divisão de treino: </strong>
              {program?.training_division}
            </RowContent>
          </ContentContainer>

          <div className="profile-code">
            <h2>Profile Code:</h2>
            <h3>{formatProfileCode(program?.datasheet?.profile_code)}</h3>
          </div>

          <div className="sheet-model">
            <h2>Modelo de ficha:</h2>
            <h3>{program?.data_model}</h3>
          </div>
        </ModalContainer>
      </Modal>
    );
  }

  const copyTraining = useCallback(async () => {
    try {
      if (possibleToCopy) {
        const idCopy = String(program?.id);

        const datasheetID = String(program?.datasheet_id);
        const datasheetModel = String(program?.data_model);

        const clipBoardValue = `${idCopy}/${datasheetID}/${datasheetModel}`;

        setClipBoard(clipBoardValue);

        toast.success('Programa de Treino copiado!');
      }
    } catch (err) {
      console.log('err:', err);

      toast.error('Ocorreu um erro ao copiar o treino!');
    }
  }, [program, possibleToCopy, setClipBoard]);

  return (
    <>
      <PageHeader>
        <HeaderWrapper>
          <PageHeaderBackButton>
            <PageHeaderContainer onClick={handleGoBack}>
              <IoIosArrowBack />

              <PageHeaderBackButtonLabel>
                divisões de treino
              </PageHeaderBackButtonLabel>
            </PageHeaderContainer>

            <IoCopy onClick={copyTraining} />
          </PageHeaderBackButton>

          <Info>
            <InfoContent>
              <strong>Nome: </strong>
              <p>{studentData.name}</p>
            </InfoContent>

            <StudentInfoModal />

            <InfoButton
              type="button"
              onClick={() => setShowStudentInfoModal(true)}
            >
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </InfoButton>
          </Info>
        </HeaderWrapper>
      </PageHeader>

      {loading ? (
        <AlertContainer>
          <MoonLoader size={30} color="#FFDF22" />
        </AlertContainer>
      ) : (
        <Container>
          {divisions?.length > 0 && (
            <Section>
              {divisions?.map((division) => {
                return (
                  <Card
                    program={division ?? ({} as DivisionDataResults)}
                    studentId={studentId}
                    billingId={billingId}
                    trainingProgramId={trainingProgramId}
                    updateDivisions={handleGetDivisions}
                  />
                );
              })}
            </Section>
          )}
        </Container>
      )}
    </>
  );
}
